import { Loader2, Play, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { adminClient, type IntegrationDraft, type IntegrationDraftOperation, type IntegrationTestResult } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SecretInput } from '@/components/ui/secret-input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { authSignsIn, bindingSettings, errorMessage, orgHoldsCredentials } from '../builderModel';
import { Field } from '../builderUi';

function pathParams(path: string): string[] {
  return [...path.matchAll(/\{([^}]+)\}/g)].map((match) => match[1]);
}

/**
 * Call one operation for real with the stored credentials. Not metered and not
 * visible to any organization — it is the author checking their configuration.
 */
export function TestOperationDialog({
  draft,
  operation,
  onClose,
}: {
  draft: IntegrationDraft;
  operation: IntegrationDraftOperation | undefined;
  onClose: () => void;
}) {
  const params = useMemo(() => (operation ? pathParams(operation.path) : []), [operation]);
  const [pathValues, setPathValues] = useState<Record<string, string>>({});
  const [query, setQuery] = useState<Array<{ key: string; value: string }>>([]);
  const [body, setBody] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<IntegrationTestResult>();
  const [error, setError] = useState<string>();

  const takesBody = operation ? !['GET', 'HEAD', 'DELETE'].includes(operation.method) : false;
  const scope = draft.connectionScope ?? 'platform';
  const oauth = authSignsIn(draft.auth);
  const orgHeld = orgHoldsCredentials({ connectionScope: scope, auth: draft.auth, authModes: draft.authModes });
  // Org and user connectors have no account at the platform: the author pastes one for this call.
  const oneOffKeys = oauth ? ['ACCESS_TOKEN'] : orgHeld ? draft.credentials.map((field) => field.key) : [];
  const [oneOff, setOneOff] = useState<Record<string, string>>({});
  // A test has no calling organization, so a bound setting takes a value typed here.
  const settingKeys = bindingSettings(operation);
  const [settingValues, setSettingValues] = useState<Record<string, string>>({});
  const missingSecrets = orgHeld
    ? []
    : draft.credentials.filter((field) => field.required && !draft.secrets.some((secret) => secret.key === field.key));

  const run = async () => {
    if (!operation) return;
    setRunning(true);
    setError(undefined);
    setResult(undefined);
    try {
      let parsedBody: unknown;
      if (takesBody && body.trim()) {
        try {
          parsedBody = JSON.parse(body);
        } catch {
          throw new Error('The body is not valid JSON.');
        }
      }
      const response = await adminClient.testIntegrationDraftOperation(draft.draftId, {
        operationId: operation.operationId,
        path: pathValues,
        query: Object.fromEntries(query.filter((row) => row.key.trim()).map((row) => [row.key.trim(), row.value])),
        ...(parsedBody !== undefined ? { body: parsedBody } : {}),
        ...(operation.kind === 'write' ? { idempotencyKey } : {}),
        ...(oneOffKeys.length
          ? { testCredentials: Object.fromEntries(oneOffKeys.filter((key) => oneOff[key]?.trim()).map((key) => [key, oneOff[key].trim()])) }
          : {}),
        ...(settingKeys.length
          ? { testSettings: Object.fromEntries(settingKeys.filter((key) => settingValues[key]?.trim()).map((key) => [key, settingValues[key].trim()])) }
          : {}),
      });
      setResult(response);
      if (operation.kind === 'write') setIdempotencyKey(crypto.randomUUID());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog
      open={Boolean(operation)}
      onClose={onClose}
      title={operation ? `Test ${operation.operationId}` : 'Test operation'}
      description={
        operation ? (
          <span className="font-mono text-xs">
            {operation.method} {draft.contract?.baseUrl}
            {operation.path}
          </span>
        ) : null
      }
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => void run()} disabled={running || !operation || params.some((param) => !pathValues[param]?.trim())}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            Send request
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {missingSecrets.length ? (
          <p className="rounded-lg border border-warning/30 bg-warning-bg/40 px-3 py-2 text-xs text-warning-foreground">
            {missingSecrets.map((field) => field.key).join(', ')} {missingSecrets.length === 1 ? 'is' : 'are'} not stored yet, so the service
            will probably refuse the call. Store credentials on the Connection step.
          </p>
        ) : null}
        {oneOffKeys.length ? (
          <div className="space-y-3 rounded-lg border border-border bg-card/40 p-3">
            <p className="text-xs text-muted-foreground">
              {oauth
                ? 'Customers connect their own accounts, so paste an access token from a test account. It is used for this call only and never stored.'
                : 'Each organization enters its own values, so paste test values. They are used for this call only and never stored.'}
            </p>
            {oneOffKeys.map((key) => (
              <Field key={key} id={`test-${key}`} label={key}>
                <SecretInput
                  id={`test-${key}`}
                  value={oneOff[key] ?? ''}
                  placeholder="Paste a test value"
                  onChange={(event) => setOneOff((current) => ({ ...current, [key]: event.target.value }))}
                  className="font-mono text-xs"
                />
              </Field>
            ))}
          </div>
        ) : null}
        {settingKeys.length ? (
          <div className="space-y-3 rounded-lg border border-border bg-card/40 p-3">
            <p className="text-xs text-muted-foreground">
              This operation binds org settings. Studio fills them from each organization&apos;s values; type the value to test with.
            </p>
            {settingKeys.map((key) => (
              <Field key={key} id={`setting-${key}`} label={key}>
                <Input
                  id={`setting-${key}`}
                  value={settingValues[key] ?? ''}
                  onChange={(event) => setSettingValues((current) => ({ ...current, [key]: event.target.value }))}
                  className="font-mono text-xs"
                />
              </Field>
            ))}
          </div>
        ) : null}
        {operation?.kind === 'write' ? (
          <p className="rounded-lg border border-border bg-card/40 px-3 py-2 text-xs text-muted-foreground">
            This operation changes data at the real service. The request carries a fresh idempotency key each time.
          </p>
        ) : null}
        {params.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {params.map((param) => (
              <Field key={param} id={`path-${param}`} label={`{${param}}`}>
                <Input
                  id={`path-${param}`}
                  value={pathValues[param] ?? ''}
                  onChange={(event) => setPathValues((current) => ({ ...current, [param]: event.target.value }))}
                  className="font-mono text-sm"
                />
              </Field>
            ))}
          </div>
        ) : null}
        <Field label="Query parameters" optional>
          <div className="space-y-2">
            {query.map((row, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  aria-label="Query name"
                  value={row.key}
                  onChange={(event) => setQuery((current) => current.map((item, i) => (i === index ? { ...item, key: event.target.value } : item)))}
                  placeholder="q"
                  className="font-mono text-xs"
                />
                <Input
                  aria-label="Query value"
                  value={row.value}
                  onChange={(event) => setQuery((current) => current.map((item, i) => (i === index ? { ...item, value: event.target.value } : item)))}
                  placeholder="value"
                  className="font-mono text-xs"
                />
                <Button variant="ghost" size="icon" aria-label="Remove parameter" onClick={() => setQuery((current) => current.filter((_, i) => i !== index))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setQuery((current) => [...current, { key: '', value: '' }])}>
              <Plus className="mr-2 h-3.5 w-3.5" />
              Add parameter
            </Button>
          </div>
        </Field>
        {takesBody ? (
          <Field id="test-body" label="JSON body" optional>
            <Textarea id="test-body" value={body} onChange={(event) => setBody(event.target.value)} placeholder='{ "name": "Ada" }' className="min-h-24 font-mono text-xs" />
          </Field>
        ) : null}
        {error ? <div className="rounded-lg border border-destructive-border bg-destructive-bg px-3 py-2 text-sm text-destructive-foreground">{error}</div> : null}
        {result ? (
          <div className="overflow-hidden rounded-xl border border-border">
            <div className="flex items-center justify-between bg-card/60 px-4 py-2 text-xs">
              <span className={cn('font-mono font-semibold', result.status < 300 ? 'text-success' : result.status < 500 ? 'text-warning' : 'text-destructive')}>
                {result.status}
              </span>
              <span className="text-muted-foreground">
                {result.durationMs} ms · {result.contentType || 'no content type'}
              </span>
            </div>
            <pre className="max-h-72 overflow-auto bg-background p-4 font-mono text-[11px] leading-5 text-muted-foreground">
              {typeof result.data === 'string' ? result.data || '(empty)' : JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
